import { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, X, ChevronLeft, ChevronRight, FolderKanban, RefreshCw } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';
import ProjectDetailPanel from './ProjectDetailPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORANGE = '#ee7203';

function truncate(str, max = 22) {
  if (!str) return '—';
  return str.length > max ? str.slice(0, max) + '…' : str;
}

/**
 * Shows a user's personally assigned projects in the same tab + detail-panel
 * layout as the main Project Tracking page.
 *
 * Props:
 *   projects      – array of { project_id, project_name, ... } (from user_assigned_projects)
 *   user          – current logged-in user
 *   hubMode       – 'my' | 'team'
 *   targetUserId  – user whose list is being shown (for remove action)
 *   isManager     – whether the viewer is a manager (controls remove action endpoint)
 *   onProjectRemoved – fn(project_id) – called after a successful remove
 *   onRefresh     – fn() – called when the user clicks Refresh
 *   loading       – bool
 */
export default function MyProjectsHubView({
  projects = [],
  user,
  hubMode,
  targetUserId,
  isManager,
  onProjectRemoved,
  onRefresh,
  loading,
}) {
  const projectIds = (projects || []).map(p => p.project_id);
  const projectNameMap = Object.fromEntries((projects || []).map(p => [p.project_id, p.project_name]));

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [projectsData, setProjectsData] = useState({}); // id → { loading, project, error }
  const [removingId, setRemovingId] = useState(null);

  // Tab scroll state
  const tabsRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = tabsRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState);
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', updateScrollState); ro.disconnect(); };
  }, [updateScrollState, projectIds.join(',')]); // eslint-disable-line

  const scrollTabs = (dir) => {
    tabsRef.current?.scrollBy({ left: dir * 200, behavior: 'smooth' });
  };

  const fetchProject = useCallback(async (id) => {
    if (projectsData[id]?.project || projectsData[id]?.loading) return;
    setProjectsData(prev => ({ ...prev, [id]: { loading: true } }));
    try {
      const res = await api.get(`${API}/zoho/project/${id}`);
      setProjectsData(prev => ({ ...prev, [id]: { loading: false, project: res.data.project } }));
    } catch {
      setProjectsData(prev => ({ ...prev, [id]: { loading: false, error: true } }));
    }
  }, [projectsData]); // eslint-disable-line

  // Fetch active project + first batch on mount/change
  useEffect(() => {
    if (projectIds.length === 0) return;
    // Always fetch active tab first
    const activeId = projectIds[Math.min(activeTabIndex, projectIds.length - 1)];
    if (activeId) fetchProject(activeId);
    // Pre-fetch first 4 others
    projectIds.slice(0, 4).filter(id => id !== activeId).forEach(id => fetchProject(id));
  }, [projectIds.join(','), activeTabIndex]); // eslint-disable-line

  // Reset to first tab when project list changes
  useEffect(() => {
    setActiveTabIndex(0);
  }, [projectIds.join(',')]); // eslint-disable-line

  const handleRemove = async (projectId) => {
    if (!window.confirm('Remove this project from your personal list?')) return;
    setRemovingId(projectId);
    try {
      const token = localStorage.getItem('token');
      const url = (hubMode === 'my' || !isManager)
        ? `${API}/project-tracking/my-assigned/${projectId}`
        : `${API}/project-tracking/user-assigned/${targetUserId}/${projectId}`;
      const r = await fetch(url, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('Failed');
      toast.success('Project removed from list');
      onProjectRemoved?.(projectId);
      // Adjust active tab index
      setActiveTabIndex(prev => Math.max(0, prev - 1));
    } catch {
      toast.error('Failed to remove project');
    } finally {
      setRemovingId(null);
    }
  };

  // ── Empty state ────────────────────────────────────────────────────────────
  if (!loading && projectIds.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm py-16 text-center">
        <FolderKanban className="w-12 h-12 mx-auto mb-3 text-gray-200" />
        <p className="text-sm font-semibold text-gray-500 mb-1">No projects assigned yet</p>
        <p className="text-xs text-gray-400 max-w-xs mx-auto">
          {hubMode === 'my'
            ? 'Projects auto-assign when you create tasks, or ask your manager to assign them.'
            : 'No projects have been assigned to this team member yet.'}
        </p>
        <button onClick={onRefresh} className="mt-4 flex items-center gap-1.5 mx-auto text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors" style={{ borderColor: ORANGE, color: ORANGE }}>
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex items-center justify-center py-16 gap-2 text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: ORANGE }} />
        <span className="text-sm">Loading projects…</span>
      </div>
    );
  }

  const safeIndex = Math.min(activeTabIndex, projectIds.length - 1);
  const activeId = projectIds[safeIndex];
  const activeEntry = projectsData[activeId] || {};

  const tabLabel = (id) => {
    const entry = projectsData[id];
    if (!entry || entry.loading) return projectNameMap[id] ? truncate(projectNameMap[id], 22) : '…';
    if (entry.error) return projectNameMap[id] ? truncate(projectNameMap[id], 22) : 'Error';
    return truncate(entry.project?.name || projectNameMap[id] || '—', 22);
  };

  return (
    <div className="project-tab-view">
      {/* Combined tab bar */}
      <div className="bg-white border-b border-gray-200 shadow-sm print:hidden flex items-end">

        {canScrollLeft && (
          <button onClick={() => scrollTabs(-1)}
            className="flex-shrink-0 self-center mb-1 ml-2 p-1 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
            data-testid="mypt-tabs-scroll-left">
            <ChevronLeft className="w-4 h-4" />
          </button>
        )}

        {/* Tabs */}
        <div ref={tabsRef}
          className="flex gap-1 px-4 pt-2 flex-1 min-w-0 overflow-x-auto [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {projectIds.map((id, i) => (
            <button
              key={id}
              onClick={() => { setActiveTabIndex(i); fetchProject(id); }}
              className={`flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold rounded-t-xl border whitespace-nowrap transition-all flex-shrink-0 ${
                i === safeIndex
                  ? 'border-gray-200 border-b-white text-white -mb-px'
                  : 'bg-gray-50 border-transparent text-gray-600 hover:text-gray-800 hover:bg-orange-50'
              }`}
              style={i === safeIndex ? { backgroundColor: ORANGE, borderColor: ORANGE, borderBottomColor: 'white' } : {}}
              data-testid={`mypt-tab-${i}`}
            >
              <span className="max-w-[150px] truncate">{tabLabel(id)}</span>
              {i === safeIndex && (
                <span
                  role="button"
                  onClick={e => { e.stopPropagation(); handleRemove(id); }}
                  className={`ml-1 opacity-40 hover:opacity-100 transition-opacity ${removingId === id ? 'animate-spin' : ''}`}
                  title="Remove from my list"
                  data-testid={`mypt-tab-remove-${i}`}
                >
                  {removingId === id
                    ? <Loader2 className="w-3 h-3" />
                    : <X className="w-3 h-3" />
                  }
                </span>
              )}
            </button>
          ))}
        </div>

        {canScrollRight && (
          <button onClick={() => scrollTabs(1)}
            className="flex-shrink-0 self-center mb-1 mr-1 p-1 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
            data-testid="mypt-tabs-scroll-right">
            <ChevronRight className="w-4 h-4" />
          </button>
        )}

        {/* Refresh in toolbar */}
        <button onClick={onRefresh}
          className="flex-shrink-0 self-center mb-1.5 mr-3 flex items-center gap-1 px-3 py-1.5 rounded-xl border text-xs font-semibold transition-colors"
          style={{ borderColor: ORANGE, color: ORANGE }}
          data-testid="mypt-refresh-btn">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* Project detail area */}
      <div>
        {activeEntry.loading && (
          <div className="flex items-center justify-center py-20 gap-2 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: ORANGE }} />
            <span className="text-sm">Loading project…</span>
          </div>
        )}
        {activeEntry.error && (
          <div className="text-center py-16 text-gray-400">
            <p className="text-sm mb-3">Failed to load project from Zoho</p>
            <button
              onClick={() => {
                setProjectsData(p => { const n = { ...p }; delete n[activeId]; return n; });
                fetchProject(activeId);
              }}
              className="text-xs underline"
              style={{ color: ORANGE }}
            >
              Retry
            </button>
          </div>
        )}
        {!activeEntry.loading && !activeEntry.error && activeEntry.project && (
          <ProjectDetailPanel
            project={activeEntry.project}
            user={user}
          />
        )}
        {/* Fallback while Zoho data hasn't loaded yet but we have stored name */}
        {!activeEntry.loading && !activeEntry.error && !activeEntry.project && activeId && (
          <div className="flex items-center justify-center py-20 gap-2 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: ORANGE }} />
            <span className="text-sm">Loading project data…</span>
          </div>
        )}
      </div>
    </div>
  );
}
